import { Box, ButtonBase, Typography, makeStyles } from "@material-ui/core";
import React, { useMemo } from "react";

import { LimitedProperties } from "@/api/generated";
import { homeCardStyle, HomeRail, HomeSection } from "@/components/home/HomeSection";
import { layoutTokens, semanticColors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";

import NoImage from "@assets/image/NoImage.svg";
import PropertySampleImage from "@assets/image/property_sample_image01.png";
import PropertySampleImage02 from "@assets/image/property_sample_image02.png";
import PropertySampleImage03 from "@assets/image/property_sample_image03.png";

const useStyles = makeStyles({
  card: {
    ...homeCardStyle,
    width: 200,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    textAlign: "left",
  },
  pcCard: { width: 320 },
  image: { width: "100%", height: 128, objectFit: "cover", backgroundColor: semanticColors["field/bg"] },
  pcImage: { height: 196 },
  body: {
    width: "100%",
    padding: layoutTokens.space[12],
    display: "flex",
    flexDirection: "column",
    gap: layoutTokens.space[8],
  },
  name: {
    ...textStyles["SP/Heading/H4"],
    color: semanticColors["text/primary"],
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  area: { ...textStyles["SP/Body/Small"], color: semanticColors["text/secondary"], overflowWrap: "anywhere" },
  price: { ...textStyles["Number/XS"], color: semanticColors["text/primary"] },
  pcPrice: { ...textStyles["Number/M"] },
  label: { ...textStyles["SP/Caption"], color: semanticColors["text/secondary"], marginBottom: layoutTokens.space[4] },
  unit: { ...textStyles["SP/Body/Small"], marginLeft: layoutTokens.space[4] },
  numbers: { display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: layoutTokens.space[8] },
});

export type LimitedPropertyWithAgent = LimitedProperties & { agent_id?: number | null };

export const LimitedPropertiesSection: React.FC<{
  properties: LimitedPropertyWithAgent[];
  onClickProperty: (property: LimitedPropertyWithAgent) => void;
  onClickViewAll: () => void;
}> = ({ properties, onClickProperty, onClickViewAll }) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const fallbackImages = useMemo(() => {
    const samples = [PropertySampleImage, PropertySampleImage02, PropertySampleImage03];
    return properties.map(() => samples[Math.floor(Math.random() * samples.length)]);
  }, [properties]);
  if (!properties.length) return null;
  const price = (property: LimitedPropertyWithAgent) => (
    <Typography className={`${classes.price} ${isMobile ? "" : classes.pcPrice}`}>
      {property.property_price == null
        ? "-"
        : (property.property_price / 10000).toLocaleString(undefined, { maximumFractionDigits: 1 })}
      <span className={classes.unit}>{property.property_price == null ? "" : "万円"}</span>
    </Typography>
  );
  return (
    <HomeSection title="プロパリー限定公開物件" onViewAll={onClickViewAll}>
      <HomeRail label="プロパリー限定公開物件">
        {properties.map((property, index) => (
          <ButtonBase
            key={`${property.property_name ?? "property"}-${index}`}
            className={`${classes.card} ${isMobile ? "" : classes.pcCard}`}
            onClick={() => onClickProperty(property)}
          >
            <img
              className={`${classes.image} ${isMobile ? "" : classes.pcImage}`}
              src={
                (property.file_url || property.object_storage_path) &&
                (property.file_url || property.object_storage_path) !== NoImage
                  ? property.file_url || property.object_storage_path || undefined
                  : fallbackImages[index]
              }
              alt=""
              loading="lazy"
            />
            <Box className={classes.body}>
              {!isMobile && price(property)}
              <Typography className={classes.name}>{property.property_name || "-"}</Typography>
              <Typography className={classes.area}>{property.property_area || "-"}</Typography>
              {!isMobile && property.property_monthly_rent != null && (
                <Typography className={classes.area}>
                  家賃 {property.property_monthly_rent.toLocaleString()}円
                </Typography>
              )}
              {isMobile && (
                <Box className={classes.numbers}>
                  {property.property_monthly_rent != null && (
                    <Box>
                      <Typography className={classes.label}>家賃収入</Typography>
                      <Typography className={classes.price}>
                        {property.property_monthly_rent.toLocaleString()}
                        <span className={classes.unit}>円/月</span>
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography className={classes.label}>価格</Typography>
                    {price(property)}
                  </Box>
                </Box>
              )}
            </Box>
          </ButtonBase>
        ))}
      </HomeRail>
    </HomeSection>
  );
};
